<template>
  <main class="page">
    <header class="page-header">
      <div class="header-inner">
        <img :src="`${baseUrl}brand-icon.svg`" alt="" width="40" height="40">
        <h1>云手机</h1>
        <router-link class="account-link" to="/profile" aria-label="我的账号"><el-icon><User /></el-icon><span>{{ user.username }}</span></router-link>
      </div>
    </header>

    <section class="device-content">
      <div class="device-create-row"><el-button type="primary" :icon="Plus" :disabled="loading || openingDeviceId !== null" @click="openManagement()">新增云手机</el-button></div>
      <div class="device-toolbar">
        <div class="status-tabs" role="tablist" aria-label="设备状态">
          <button v-for="tab in statusTabs" :key="tab.key" role="tab" :aria-selected="statusFilter === tab.key" @click="statusFilter = tab.key">{{ tab.label }}<span>{{ tab.count }}</span></button>
        </div>
        <el-tooltip content="刷新云机列表"><el-button :icon="Refresh" circle :loading="loading" :disabled="openingDeviceId !== null" aria-label="刷新云机列表" @click="loadDevices" /></el-tooltip>
      </div>

      <p v-if="loadError && devices.length" class="list-error" role="alert">{{ loadError }}</p>
      <div v-if="loading && devices.length === 0" class="device-grid" aria-label="正在读取云手机" aria-busy="true">
        <div v-for="index in 4" :key="index" class="device-placeholder"><el-skeleton animated><template #template><el-skeleton-item variant="image" class="preview-placeholder" /><el-skeleton-item variant="text" /></template></el-skeleton></div>
      </div>

      <el-result v-else-if="loadError && devices.length === 0" icon="warning" title="暂时无法读取云手机" :sub-title="loadError"><template #extra><el-button type="primary" @click="loadDevices">重新加载</el-button></template></el-result>
      <el-empty v-else-if="visibleDevices.length === 0" :description="devices.length ? '当前状态下暂无云手机' : '暂无已分配的云手机'" />

      <div v-else class="device-grid">
        <article v-for="device in visibleDevices" :key="device.deviceId" class="device-card">
          <div class="device-visual">
            <NativeCloudPhonePreview v-if="device.deviceStatus === 'ONLINE' && (!recoveries[device.deviceId] || recoveries[device.deviceId].phase === 'connecting')" :key="`${device.deviceId}-${previewVersions[device.deviceId] || 0}`" :ref="instance => setPreviewRef(device.deviceId, instance)" :device-id="device.deviceId" @preview-error="handlePreviewError" @preview-status="handlePreviewStatus" />
            <div v-else class="offline-state"><el-icon><Cellphone /></el-icon><span>设备离线</span></div>
            <button class="device-open" :disabled="device.deviceStatus !== 'ONLINE' || openingDeviceId !== null || !!recoveries[device.deviceId]" :aria-label="`进入${device.deviceName || '云手机'}`" @click="openDevice(device)">
              <span v-if="openingDeviceId === device.deviceId" class="opening-state"><el-icon class="is-loading"><Loading /></el-icon>正在进入</span>
              <span v-else class="enter-label">进入云手机<el-icon><ArrowRight /></el-icon></span>
            </button>
            <div v-if="recoveries[device.deviceId]" class="recovery-state" role="status">
              <el-icon v-if="recoveries[device.deviceId].phase !== 'failed'" class="is-loading"><Loading /></el-icon>
              <span>{{ recoveries[device.deviceId].message }}</span>
              <el-button v-if="recoveries[device.deviceId].phase === 'failed'" size="small" @click="retryRecovery(device.deviceId)">重试连接</el-button>
            </div>
          </div>
          <div class="device-details">
            <div class="device-title"><el-icon><Cellphone /></el-icon><h2 :title="device.deviceName">{{ device.deviceName || `云手机 ${device.deviceId}` }}</h2><span class="status-dot" :class="{ online: device.deviceStatus === 'ONLINE' }" :title="device.deviceStatus === 'ONLINE' ? '在线' : '离线'"></span></div>
            <div class="device-meta"><p :title="device.expireTime">{{ expiryText(device.expireTime) }}</p><el-tooltip content="云机设置" :enterable="false"><el-button class="device-settings" :icon="Setting" text circle :disabled="loading || openingDeviceId !== null" :aria-label="`${device.deviceName || '云手机'}设置`" @click.stop="openManagement(device)" /></el-tooltip></div>
          </div>
        </article>
      </div>
    </section>
    <CloudDeviceManagementDialog v-model="managementVisible" :device="managementTarget" :devices="devices" :recovery-message="recoveries[managementTarget?.deviceId]?.message || ''" @renamed="handleRenamed" @redeemed="handleRedeemed" @lifecycle="handleLifecycle" @upload="openUpload" />
    <CloudPhoneUploadDialog v-if="uploadTarget" :key="uploadTarget.deviceId" v-model="uploadVisible" :device-id="uploadTarget.deviceId" />
    <AppNavigation />
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { ArrowRight, Cellphone, Loading, Plus, Refresh, Setting, User } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'
import { fetchCloudDevice, fetchMyCloudDevices } from '../api/cloudDevice'
import { getRequestErrorMessage } from '../api/http'
import { getUser } from '../utils/authSession'
import AppNavigation from '../components/AppNavigation.vue'
import NativeCloudPhonePreview from '../components/cloudphone/NativeCloudPhonePreview.vue'
import CloudDeviceManagementDialog from '../components/cloudphone/CloudDeviceManagementDialog.vue'
import CloudPhoneUploadDialog from '../components/cloudphone/CloudPhoneUploadDialog.vue'

const router = useRouter()
const baseUrl = process.env.BASE_URL
const user = getUser()
const devices = ref([])
const loading = ref(false)
const loadError = ref('')
const statusFilter = ref('all')
const openingDeviceId = ref(null)
const previewRefs = new Map()
const previewErrorTimes = new Map()
const managementVisible = ref(false)
const managementDeviceId = ref(null)
const managementTarget = computed(() => devices.value.find(device => device.deviceId === managementDeviceId.value) || null)
const uploadTarget = ref(null)
const uploadVisible = ref(false)
const recoveries = ref({})
const previewVersions = ref({})
const recoveryTimers = new Map()
const recoveryDelays = [8000, 8000, 12000, 16000]
let disposed = false
const onlineCount = computed(() => devices.value.filter(device => device.deviceStatus === 'ONLINE').length)
const statusTabs = computed(() => [
  { key: 'all', label: '全部', count: devices.value.length },
  { key: 'online', label: '在线', count: onlineCount.value },
  { key: 'offline', label: '离线', count: devices.value.length - onlineCount.value }
])
const visibleDevices = computed(() => devices.value.filter(device => statusFilter.value === 'all'
  || (device.deviceStatus === 'ONLINE') === (statusFilter.value === 'online')))

function expiryText(value) {
  if (!value) return '到期时间未设置'
  return `${value.replace('T', ' ').slice(0, 16)} 到期`
}

async function loadDevices() {
  if (loading.value) return
  loading.value = true
  loadError.value = ''
  try {
    devices.value = await fetchMyCloudDevices() || []
  } catch (error) {
    loadError.value = getRequestErrorMessage(error, '读取设备列表失败')
  } finally {
    loading.value = false
  }
}

function setPreviewRef(deviceId, instance) {
  const key = String(deviceId)
  if (instance) previewRefs.set(key, instance)
  else previewRefs.delete(key)
}

async function openDevice(device) {
  if (openingDeviceId.value !== null || recoveries.value[device.deviceId] || device.deviceStatus !== 'ONLINE') return
  openingDeviceId.value = device.deviceId
  try {
    await previewRefs.get(String(device.deviceId))?.closePreviewForDetail?.()
    await router.push(`/devices/${device.deviceId}`)
  } catch (error) {
    ElMessage.error(getRequestErrorMessage(error, '进入云手机失败'))
  } finally {
    openingDeviceId.value = null
  }
}

function handlePreviewError({ deviceId, message }) {
  if (recoveries.value[deviceId]?.phase === 'connecting') scheduleRecovery(deviceId)
  const now = Date.now()
  const key = String(deviceId)
  if (now - (previewErrorTimes.get(key) || 0) < 2000) return
  previewErrorTimes.set(key, now)
  console.error('[Native preview error]', { deviceId, message })
}

function openManagement(device = null) {
  managementDeviceId.value = device?.deviceId ?? null
  managementVisible.value = true
}

function handleRenamed({ deviceId, deviceName }) {
  devices.value = devices.value.map(device => device.deviceId === deviceId ? { ...device, deviceName } : device)
}

function handleRedeemed({ isNew }) {
  if (isNew) statusFilter.value = 'all'
  void loadDevices()
}

function openUpload(device) {
  uploadTarget.value = device
  uploadVisible.value = true
}

function clearRecoveryTimer(deviceId) {
  window.clearTimeout(recoveryTimers.get(String(deviceId)))
  recoveryTimers.delete(String(deviceId))
}

function finishRecovery(deviceId) {
  clearRecoveryTimer(deviceId)
  delete recoveries.value[deviceId]
}

function handlePreviewStatus({ deviceId, status }) {
  if (recoveries.value[deviceId]?.phase !== 'connecting') return
  if (status === 'playing') finishRecovery(deviceId)
  else if (status === 'error') scheduleRecovery(deviceId)
}

function scheduleRecovery(deviceId) {
  clearRecoveryTimer(deviceId)
  const recovery = recoveries.value[deviceId]
  if (!recovery || disposed) return
  if (recovery.attempt >= recoveryDelays.length) {
    recovery.phase = 'failed'
    recovery.message = '暂未恢复，请稍后重试连接'
    return
  }
  recovery.phase = 'waiting'
  if (document.visibilityState === 'hidden') return
  recoveryTimers.set(String(deviceId), window.setTimeout(async () => {
    recoveryTimers.delete(String(deviceId))
    if (disposed || recoveries.value[deviceId] !== recovery) return
    if (document.visibilityState === 'hidden') return
    recovery.attempt++
    try {
      const device = await fetchCloudDevice(deviceId)
      if (disposed || recoveries.value[deviceId] !== recovery) return
      if (!device || String(device.deviceId) !== String(deviceId)) throw new Error('云机状态暂不可用')
      devices.value = devices.value.map(item => item.deviceId === deviceId ? device : item)
      if (device.deviceStatus !== 'ONLINE' || document.visibilityState === 'hidden') {
        scheduleRecovery(deviceId)
        return
      }
      previewVersions.value[deviceId] = (previewVersions.value[deviceId] || 0) + 1
      recovery.phase = 'connecting'
      // ONLINE alone is not proof that a rebooted media stream is ready.
      // Wait for the preview's actual playing status before allowing entry.
      recoveryTimers.set(String(deviceId), window.setTimeout(() => scheduleRecovery(deviceId), 20000))
    } catch {
      if (!disposed && recoveries.value[deviceId] === recovery) scheduleRecovery(deviceId)
    }
  }, recoveryDelays[recovery.attempt]))
}

async function handleLifecycle({ deviceId, action }) {
  const preview = previewRefs.get(String(deviceId))
  recoveries.value[deviceId] = { attempt: 0, phase: 'waiting', message: action === 'restart' ? '云机正在重新启动' : '云机正在恢复出厂设置' }
  try {
    await preview?.closePreviewForDetail?.()
  } catch (error) {
    console.warn('释放重启前的预览会话失败', error)
  }
  if (!disposed) scheduleRecovery(deviceId)
}

function retryRecovery(deviceId) {
  const recovery = recoveries.value[deviceId]
  if (!recovery || recovery.phase !== 'failed') return
  recovery.attempt = 0
  recovery.message = '正在重新连接预览'
  scheduleRecovery(deviceId)
}

function resumeRecoveries() {
  if (document.visibilityState !== 'visible') return
  for (const device of devices.value) {
    const recovery = recoveries.value[device.deviceId]
    if (recovery?.phase === 'waiting' && !recoveryTimers.has(String(device.deviceId))) scheduleRecovery(device.deviceId)
  }
}

onMounted(() => {
  void loadDevices()
  document.addEventListener('visibilitychange', resumeRecoveries)
})
onBeforeUnmount(() => {
  disposed = true
  recoveryTimers.forEach(timer => window.clearTimeout(timer))
  recoveryTimers.clear()
  document.removeEventListener('visibilitychange', resumeRecoveries)
})
</script>

<style scoped>
.page { min-height: 100vh; min-height: 100svh; padding-bottom: calc(90px + env(safe-area-inset-bottom, 0px)); background: #f6f6f9; }
.page-header { padding: env(safe-area-inset-top, 0px) 20px 0; background: #fff; border-bottom: 1px solid #eceef0; }
.header-inner { display: flex; align-items: center; gap: 10px; max-width: 1080px; min-height: 78px; margin: 0 auto; }
.header-inner img { object-fit: contain; border-radius: 10px; }
h1 { flex: 1; margin: 0; font-size: 25px; font-weight: 650; }
.account-link { display: flex; align-items: center; gap: 6px; max-width: 45%; color: #607574; font-size: 13px; }
.account-link span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.account-link .el-icon { flex-shrink: 0; font-size: 20px; }
.device-content { max-width: 1120px; margin: auto; padding: 22px 20px; }
.device-create-row { margin-bottom: 16px; }
.device-create-row .el-button { min-height: 40px; }
.list-error { color: #c93949; font-size: 13px; }
.device-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 20px; }
.status-tabs { display: flex; align-items: center; gap: 7px; }
.status-tabs button { display: flex; align-items: center; gap: 5px; min-height: 36px; padding: 0 13px; border: 0; border-radius: 6px; font-size: 14px; color: #747881; background: transparent; cursor: pointer; }
.status-tabs button span { font-size: 12px; }
.status-tabs button[aria-selected='true'] { background: #ddf7f4; color: #047f7b; font-weight: 600; }
.device-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 18px; }
.device-card { min-width: 0; overflow: hidden; background: #fff; border: 1px solid #e9ebee; border-radius: 14px; }
.device-visual { position: relative; width: 100%; aspect-ratio: 9 / 16; background: #202124; overflow: hidden; }
.offline-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; width: 100%; height: 100%; color: #b8bbc2; font-size: 13px; }
.offline-state .el-icon { font-size: 34px; color: #7a828a; }
.device-open { position: absolute; z-index: 2; inset: 0; width: 100%; padding: 0; background: none; border: 0; color: white; cursor: pointer; }
.device-open:disabled { cursor: default; }
.device-open:disabled .enter-label { display: none; }
.device-open:focus-visible { outline-offset: -4px; }
.enter-label { position: absolute; right: 8px; bottom: 8px; display: flex; align-items: center; gap: 4px; padding: 5px 8px; color: #fff; font-size: 11px; border-radius: 4px; background: rgba(0, 0, 0, .5); }
.opening-state { display: flex; align-items: center; justify-content: center; gap: 8px; height: 100%; font-size: 13px; background: rgba(0, 0, 0, .7); }
.recovery-state { position: absolute; inset: 0; z-index: 3; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 12px; color: #d8e2e4; background: #202124; text-align: center; font-size: 12px; line-height: 1.6; }
.device-details { padding: 11px 9px 12px; }
.device-title { display: flex; align-items: center; gap: 5px; }
.device-title .el-icon { flex-shrink: 0; color: #3877eb; background: #e7eeff; width: 21px; height: 21px; border-radius: 50%; font-size: 13px; }
h2 { min-width: 0; flex: 1; margin: 0; font-size: 13px; font-weight: 550; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.status-dot { flex-shrink: 0; width: 6px; height: 6px; border-radius: 50%; background: #b0b6bd; }
.status-dot.online { background: #10b8b1; }
.device-details p { margin: 8px 0 0; color: #858991; font-size: 11px; line-height: 1.5; overflow-wrap: anywhere; }
.device-meta { display: flex; align-items: center; gap: 4px; margin-top: 6px; }
.device-meta p { flex: 1; min-width: 0; margin: 0; }
.device-settings { flex-shrink: 0; width: 32px; height: 32px; margin: 0; color: #627875; }
.preview-placeholder { width: 100%; aspect-ratio: 9 / 16; height: auto; margin-bottom: 12px; }
@media (max-width: 1000px) { .device-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 640px) { .device-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; } .device-content { padding: 16px 14px; } .page-header { padding-right: 16px; padding-left: 16px; } .header-inner { min-height: 70px; } h1 { font-size: 23px; } .status-tabs { gap: 3px; } .status-tabs button { padding: 0 10px; font-size: 13px; } .device-toolbar { margin-bottom: 16px; } }
</style>
